import React, { useEffect, useRef } from "react";

export default function BackgroundAnimation() {
    const canvasRef = useRef(null);

    useEffect(() => {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext("2d");
        let width, height;
        let gridLines = [];
        const spacing = 120; // Slightly larger spacing for cleaner thick lines

        const resize = () => {
            width = canvas.width = window.innerWidth;
            height = canvas.height = window.innerHeight;
        };

        class GridLine {
            constructor(type, position) {
                this.type = type; // 'h' or 'v'
                this.position = position;
                this.pulse = Math.random() * 2 - 1; // Wider range for variance
                this.speed = 0.003 + Math.random() * 0.004; // Slow, deliberate flow

                // Intense Theme Colors
                this.colors = [
                    "rgba(91, 76, 255, 0.8)",  // Intense Primary Blue
                    "rgba(26, 26, 46, 0.7)",   // Bold Navy/Black
                    "rgba(0, 191, 255, 0.7)",  // Vibrant Cyan
                    "rgba(124, 58, 237, 0.6)"  // Vivid Violet
                ];
                this.accentColor = this.colors[Math.floor(Math.random() * this.colors.length)];
                this.lineWidth = Math.random() * 1 + 1; // Thicker lines (2px to 4px)
            }

            draw() {
                // 1. Draw Static Base Grid Line (Subtle but visible)
                ctx.beginPath();
                ctx.lineWidth = 0.8;
                ctx.strokeStyle = `rgba(255, 255, 255, 0.05)`;
                if (this.type === 'h') {
                    ctx.moveTo(0, this.position);
                    ctx.lineTo(width, this.position);
                } else {
                    ctx.moveTo(this.position, 0);
                    ctx.lineTo(this.position, height);
                }
                ctx.stroke();

                // 2. Draw Moving Color "Stream" (Thicker and Saturated)
                this.pulse += this.speed;
                if (this.pulse > 1.2) this.pulse = -0.5;

                const segmentLength = 400; // Longer segments
                const currentPos = this.pulse * (this.type === 'h' ? width : height);

                const gradient = this.type === 'h'
                    ? ctx.createLinearGradient(currentPos - segmentLength, 0, currentPos, 0)
                    : ctx.createLinearGradient(0, currentPos - segmentLength, 0, currentPos);

                gradient.addColorStop(0, "rgba(255, 255, 255, 0)");
                gradient.addColorStop(0.5, this.accentColor);
                gradient.addColorStop(1, "rgba(255, 255, 255, 0)");

                ctx.beginPath();
                ctx.lineWidth = this.lineWidth;
                ctx.strokeStyle = gradient;

                // Add subtle shadow to the lines for "pop"
                ctx.shadowBlur = 4;
                ctx.shadowColor = this.accentColor;

                if (this.type === 'h') {
                    ctx.moveTo(currentPos - segmentLength, this.position);
                    ctx.lineTo(currentPos, this.position);
                } else {
                    ctx.moveTo(this.position, currentPos - segmentLength);
                    ctx.lineTo(this.position, currentPos);
                }
                ctx.stroke();

                // Reset shadow for next draw
                ctx.shadowBlur = 0;
            }
        }

        const init = () => {
            resize();
            gridLines = [];
            for (let y = 0; y < height; y += spacing) {
                gridLines.push(new GridLine('h', y));
            }
            for (let x = 0; x < width; x += spacing) {
                gridLines.push(new GridLine('v', x));
            }
        };

        const animate = () => {
            ctx.clearRect(0, 0, width, height);
            gridLines.forEach(line => line.draw());
            requestAnimationFrame(animate);
        };

        init();
        animate();

        window.addEventListener("resize", init);
        return () => window.removeEventListener("resize", init);
    }, []);

    return (
        <canvas
            ref={canvasRef}
            style={{
                position: "fixed",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                zIndex: -1,
                pointerEvents: "none",
                background: "transparent",
            }}
        />
    );
}
